import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types for the training assessment
export type Client = {
  _id: string;
  name: string;
  avatar?: string;
};

export type EnergyLevel = 'often_low' | 'fluctuates' | 'often_high';

export type IntensityPreference = 'high_intensity' | 'balanced' | 'low_intensity';

export type ActivityLevel = 'mostly_sedentary' | 'lightly_active' | 'highly_active';

export type FitnessLevel =
  | 'beginner'
  | 'early_intermediate'
  | 'intermediate'
  | 'advanced'
  | 'athlete';

export type ScheduleFlexibility = 'limited' | 'moderate' | 'flexible';

export type TrainingGoal = 'build_muscle' | 'fat_loss' | 'hypertrophy' | 'performance';

export type TrainingStyle = 'weight_training' | 'functional_training';

export type MovementInsights = {
  mobilityLevel: 'poor' | 'fair' | 'good' | 'excellent';
  stabilityControl: 'poor' | 'fair' | 'good' | 'excellent';
  balanceConsistency: 'poor' | 'fair' | 'good' | 'excellent';
  movementSymmetry: 'poor' | 'fair' | 'good' | 'excellent';
  overallMovementQuality: 'poor' | 'fair' | 'good' | 'excellent';
};

export type TrainingAssessment = {
  selectedClient: Client | null;
  baselineEnergyLevel: EnergyLevel | null;
  intensityPreference: IntensityPreference | null;
  activityLevel: ActivityLevel | null;
  fitnessLevel: FitnessLevel | null;
  scheduleFlexibility: ScheduleFlexibility | null;
  trainingGoal: TrainingGoal | null;
  trainingStyle: TrainingStyle | null;
  movementInsights: MovementInsights | null;
  currentStep: number;
  isComplete: boolean;
  selectedDays: string[];
};

type TrainingState = {
  assessment: TrainingAssessment;
  availableClients: Client[];
};

const initialAssessment: TrainingAssessment = {
  selectedClient: null,
  baselineEnergyLevel: null,
  intensityPreference: null,
  activityLevel: null,
  fitnessLevel: null,
  scheduleFlexibility: null,
  trainingGoal: null,
  trainingStyle: null,
  movementInsights: null,
  currentStep: 0,
  isComplete: false,
  selectedDays: [],
};

const defaultClients: Client[] = [
  {
    _id: 'emily_demo',
    name: 'Emily Thompson (Demo)',
    avatar: 'https://i.pravatar.cc/128?img=5',
  },
  {
    _id: 'oliver_demo',
    name: 'Oliver Bennett (Demo)',
    avatar: 'https://i.pravatar.cc/128?img=3',
  },
];

export const trainingStore = create<TrainingState>()(
  persist(
    () => ({
      assessment: initialAssessment,
      availableClients: defaultClients,
    }),
    {
      name: 'training-storage',
    },
  ),
);

// Actions defined outside the store to ensure they are always available and not persisted
export const setClient = (client: Client) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, selectedClient: client },
  }));

export const setSelectedClient = (client: Client) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, selectedClient: client },
  }));

export const setSelectedDays = (days: string[]) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, selectedDays: days },
  }));

export const toggleAssessmentDay = (day: string) =>
  trainingStore.setState((state) => {
    const selectedDays = state.assessment.selectedDays || [];
    const newDays = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];
    return {
      assessment: { ...state.assessment, selectedDays: newDays },
    };
  });

export const setBaselineEnergyLevel = (level: EnergyLevel) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, baselineEnergyLevel: level },
  }));

export const setIntensityPreference = (preference: IntensityPreference) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, intensityPreference: preference },
  }));

export const setActivityLevel = (level: ActivityLevel) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, activityLevel: level },
  }));

export const setFitnessLevel = (level: FitnessLevel) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, fitnessLevel: level },
  }));

export const setScheduleFlexibility = (flexibility: ScheduleFlexibility) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, scheduleFlexibility: flexibility },
  }));

export const setTrainingGoal = (goal: TrainingGoal) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, trainingGoal: goal },
  }));

export const setTrainingStyle = (style: TrainingStyle) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, trainingStyle: style },
  }));

export const setMovementInsights = (insights: MovementInsights) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, movementInsights: insights },
  }));

export const setCurrentStep = (step: number) =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, currentStep: step },
  }));

export const nextStep = () =>
  trainingStore.setState((state) => ({
    assessment: {
      ...state.assessment,
      currentStep: Math.min(state.assessment.currentStep + 1, 8),
    },
  }));

export const previousStep = () =>
  trainingStore.setState((state) => ({
    assessment: {
      ...state.assessment,
      currentStep: Math.max(state.assessment.currentStep - 1, 0),
    },
  }));

export const completeAssessment = () =>
  trainingStore.setState((state) => ({
    assessment: { ...state.assessment, isComplete: true },
  }));

export const resetAssessment = () =>
  trainingStore.setState(() => ({
    assessment: initialAssessment,
  }));

// Utility functions
export const getStepProgress = () => {
  const { assessment } = trainingStore.getState();
  const totalSteps = 9; // 0-8 steps
  return (assessment.currentStep / (totalSteps - 1)) * 100;
};

export const isStepComplete = (step: number) => {
  const { assessment } = trainingStore.getState();
  switch (step) {
    case 0:
      return assessment.selectedClient !== null;
    case 1:
      return assessment.baselineEnergyLevel !== null;
    case 2:
      return assessment.intensityPreference !== null;
    case 3:
      return assessment.activityLevel !== null;
    case 4:
      return assessment.fitnessLevel !== null;
    case 5:
      return assessment.scheduleFlexibility !== null;
    case 6:
      return assessment.trainingGoal !== null;
    case 7:
      return assessment.trainingStyle !== null;
    case 8:
      return assessment.movementInsights !== null;
    default:
      return false;
  }
};
